<template>
  <div
    id="recentlyViewed"
    class="recently-viewed"
    data-testid="sbx-pcc_recentlyViewed"
    :style="cssColors"
    v-if="loading || recentlyViewedItems.length"
  >
    <div v-if="loading" class="loading" data-testid="sbx-pcc_recentlyViewed_loading">
      <sbx-spinner />
    </div>
    <div v-if="recentlyViewedItems.length" class="recently-viewed__content" data-testid="sbx-pcc_recentlyViewed_content">
      <h3 class="recently-viewed__title" data-testid="sbx-pcc_recentlyViewed-title">{{ getTitle }}</h3>
      <div class="recently-viewed__items" data-testid="sbx-pcc_recentlyViewed-list">
        <product-vignette
          v-for="(product, index) in recentlyViewedItems"
          :key="index"
          :product="product.item"
          :related="product.related || {}"
          :vignetteHandleClick="() => vignetteHandleClick(product, index, 'Homepage')"
          :test-id="`sbx-pcc_recentlyViewed-list-item-${index}`"
        ></product-vignette>
      </div>
    </div>
  </div>
</template>

<script>
import { recentlyViewed } from './index.js';
import ProductVignette from '@/SharedFe/components/ProductVignette/ProductVignette.vue';
import SbxSpinner from '@/SharedFe/atoms/SbxSpinner/SbxSpinner.vue';
import productListMixin from '../ProductList/mixins/productListMixin';
import { getRecentlyViewedFromCookie } from '@/SharedFe/utils/cookieUtils';
import { to } from '@/SharedFe/utils';
import { fetchProducts } from '../ProductList/api/productList';
import { collectionSuperLightGrey } from '@/SharedFe/scss/brand.module.scss';

export default {
  name: 'recentlyViewed',
  components: { SbxSpinner, ProductVignette },
  mixins: [productListMixin],
  props: {
    ...recentlyViewed.propTypes,
  },
  data() {
    return {
      recentlyViewedItems: [],
      loading: false,
    };
  },

  computed: {
    getTitle() {
      return this.title !== '' ? this.title : this.$t('recentlyViewed_title');
    },
    cssColors() {
      return {
        // eslint-disable-next-line
        '--bgColor': !!this.backgroundColor ? this.backgroundColor : collectionSuperLightGrey,
      };
    },
  },

  methods: {
    async fetchData() {
      const viewedItems = getRecentlyViewedFromCookie();
      let searchParams = '';
      if (!viewedItems.length) {
        return false;
      }

      //Call search API to get product list by pimIds using text param
      this.recentlyViewedItems = [];
      this.error = null;
      this.loading = true;
      viewedItems.forEach((item, index) => {
        searchParams += `${item.id}${index !== viewedItems.length - 1 ? ',' : ''}`;
      });

      const [err, data] = await to(fetchProducts(searchParams));

      if (err) {
        this.errorMessage = err;
        console.error('RecentlyViewed -', err);
        setTimeout(() => {
          this.loading = false;
        }, 500);
        return {};
      }

      if (data.error) {
        this.loading = false;
      }

      if (data && data.items && data.items.length < 1) {
        // error message in case of zero items to show
        const error = 'RecentlyViewed - No items founded for box search query';
        console.error(error);
        this.loading = false;
      }

      if (data.items && data.items.length >= 1) {
        this.loading = false;
        //Sort items by order in pimIds array
        this.recentlyViewedItems = data.items.sort(
          (a, b) => viewedItems.findIndex(item => item.id == a.item.id) - viewedItems.findIndex(item => item.id == b.item.id),
        );
      }
    },
  },

  mounted() {
    // fetch the data when the view is created
    this.fetchData();
  },
};
</script>
<style lang="scss" scoped>
@import '@/SharedFe/scss/variables.scss';
@import '@/SharedFe/scss/typography.scss';

$small-only: 'only screen and (max-width: 767px)';
$medium-only: 'only screen and (min-width: 768px) and (max-width: 1024px)';
$medium-up: 'only screen and (min-width: 768px)';
$large-up: 'only screen and (min-width: 1025px)';

$bgColor: var(--bgColor);
$titleColor: '#343533';

.recently-viewed {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  color: $collection-superdark-grey;
  background: $bgColor;
  padding: rem-calc(30) 0;
  @media #{$medium-up} {
    padding: rem-calc(25) 0 rem-calc(20);
  }

  &__title {
    color: $titleColor;
    margin: rem-calc(4) 0 rem-calc(18);
  }

  &__items {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    text-align: left;
    margin: 0;
    @media #{$small-only} {
      padding: 0 1rem;
      justify-content: left;
    }
  }

  ::v-deep {
    .product-vignette {
      width: 50%;

      &__title {
        font-size: rem-calc(14);
        line-height: 1rem;
      }

      &__info {
        padding: 1rem 0.5rem 0.75rem;

        h3 {
          margin-top: 0;
        }
      }

      &__description,
      &__delivery-format {
        display: none !important;
      }

      &__price-wrapper {
        height: auto;
      }

      &__bottom {
        padding: 0 0.5rem 0.5rem;
      }

      @media #{$small-only} {
        border-radius: rem-calc(3);
        &__item {
          padding: 0.5rem;
        }
        &__image > img {
          border-radius: unset;
        }
        &__info {
          padding: 1rem 0 0.75rem;
        }
        &__bottom {
          padding: 0 0 0.25rem;
        }
      }

      @media #{$medium-up} {
        width: calc(25% - 7px);
      }
      @media #{$large-up} {
        width: calc(25% - 14px);
        margin-right: rem-calc(14);
        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
}
</style>
